import { useCallback, useEffect, useRef, useState } from "react";
import { AxiomeConnect } from "@axiome/counter-lp-sdk";
import { CONNECT_API } from "../lib/config";
import { friendlyError } from "../lib/friendlyError";

const STORAGE_KEY = "axm_counter_lp_session";

type Session = { token: string; account: string };

export function useAxiomeWallet() {
  const connectRef = useRef(new AxiomeConnect(CONNECT_API));
  const [account, setAccount] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [authCode, setAuthCode] = useState<string | null>(null);
  const [qrPayload, setQrPayload] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    try {
      const s = JSON.parse(raw) as Session;
      connectRef.current.token = s.token;
      connectRef.current
        .verifySession(s.token)
        .then((addr) => {
          if (addr) {
            setAccount(addr);
            setToken(s.token);
          } else {
            localStorage.removeItem(STORAGE_KEY);
          }
        })
        .catch(() => localStorage.removeItem(STORAGE_KEY));
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const startConnect = useCallback(async () => {
    setError(null);
    setConnecting(true);
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    try {
      const info = await connectRef.current.createToken();
      setAuthCode(info.token);
      setQrPayload(connectRef.current.authQrPayload(info.token));
      const addr = await connectRef.current.waitForApproval({
        token: info.token,
        signal: abortRef.current.signal,
      });
      setAccount(addr);
      setToken(info.token);
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ token: info.token, account: addr } satisfies Session),
      );
      setAuthCode(null);
      setQrPayload(null);
    } catch (e) {
      if ((e as Error).message !== "Aborted") {
        const soft = friendlyError(e);
        if (soft) setError(soft);
      }
    } finally {
      setConnecting(false);
    }
  }, []);

  const cancelConnect = useCallback(() => {
    abortRef.current?.abort();
    setConnecting(false);
    setAuthCode(null);
    setQrPayload(null);
  }, []);

  const disconnect = useCallback(() => {
    connectRef.current.disconnect();
    localStorage.removeItem(STORAGE_KEY);
    setAccount(null);
    setToken(null);
  }, []);

  const sign = useCallback(
    async (messages: Parameters<AxiomeConnect["sign"]>[0]) => {
      if (!token) throw new Error("Сначала подключите Axiome Connect");
      try {
        return await connectRef.current.sign(messages, token);
      } catch (e) {
        throw new Error(friendlyError(e, "Подпись не прошла — попробуйте ещё раз"));
      }
    },
    [token],
  );

  return {
    account,
    token,
    connecting,
    authCode,
    qrPayload,
    error,
    startConnect,
    cancelConnect,
    disconnect,
    sign,
  };
}
